import { BUSINESS_TIMEZONE, assertBusinessDate, shiftBusinessDate } from '@/shared/time/businessTime';

export const monthNames = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

export const monthAbbrev = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

export const weekdayLabels = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

export const businessDateFormatter = new Intl.DateTimeFormat('es-ES', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: BUSINESS_TIMEZONE,
});

export const formatBusinessDateLabel = (businessDateIso: string) =>
  businessDateFormatter.format(assertBusinessDate(businessDateIso, 'mid'));

export const getMonthStartIso = (businessDateIso: string) => `${businessDateIso.slice(0, 7)}-01`;

// Cálculo del salto de mes basado 100% en el texto de la fecha (año/mes enteros).
// setUTCMonth sobre una fecha en zona horaria de negocio desincroniza el calendario
// con su encabezado en los límites de mes (bug "Mayo / Mayo").
export const shiftMonthStart = (monthStartIso: string, offset: number) => {
  const year = Number(monthStartIso.slice(0, 4));
  const monthIndex = Number(monthStartIso.slice(5, 7)) - 1 + offset;
  const targetYear = year + Math.floor(monthIndex / 12);
  const targetMonth = ((monthIndex % 12) + 12) % 12;
  return `${targetYear}-${String(targetMonth + 1).padStart(2, '0')}-01`;
};

export const getMonthEndIso = (monthStartIso: string) => shiftBusinessDate(shiftMonthStart(monthStartIso, 1), -1);

// Anclado a 'mid' (mediodía) para que el desfase de zona horaria nunca cruce la
// medianoche y corra el día de la semana una columna.
export const getMonthWeekdayIndex = (monthStartIso: string) =>
  assertBusinessDate(monthStartIso, 'mid').getUTCDay();

export const generateCalendarDays = (monthStartIso: string) => {
  const offset = getMonthWeekdayIndex(monthStartIso);
  const firstDayIso = shiftBusinessDate(monthStartIso, -offset);
  return Array.from({ length: 42 }, (_, index) => shiftBusinessDate(firstDayIso, index));
};

export const isSameBusinessMonth = (candidateIso: string, monthStartIso: string) =>
  candidateIso.slice(0, 7) === monthStartIso.slice(0, 7);

// El encabezado se deriva directamente del texto de la fecha, sin objetos Date, para
// que nunca pueda contradecir a los días que muestra el calendario.
export const getMonthLabel = (monthStartIso: string) => {
  const year = monthStartIso.slice(0, 4);
  const monthIndex = Number(monthStartIso.slice(5, 7)) - 1;
  return `${monthNames[monthIndex]} ${year}`;
};

export const isDateDisabled = (iso: string, minDate?: string | null, maxDate?: string | null) => {
  if (minDate && iso < minDate) return true;
  if (maxDate && iso > maxDate) return true;
  return false;
};

export const isMonthOutOfRange = (
  year: number,
  monthIndex: number,
  minDate?: string | null,
  maxDate?: string | null,
) => {
  const monthStart = `${year}-${String(monthIndex + 1).padStart(2, '0')}-01`;
  const monthEnd = getMonthEndIso(monthStart);
  if (minDate && monthEnd < minDate) return true;
  if (maxDate && monthStart > maxDate) return true;
  return false;
};

export const isYearOutOfRange = (year: number, minDate?: string | null, maxDate?: string | null) => {
  const yearStart = `${year}-01-01`;
  const yearEnd = `${year}-12-31`;
  if (minDate && yearEnd < minDate) return true;
  if (maxDate && yearStart > maxDate) return true;
  return false;
};
